'use client';

import { Avatar, Button } from '@lectura/ui';
import type { Route } from 'next';
import { BarChart3, LogOut, ShieldCheck, User } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useHydrated } from '@/lib/use-hydrated';

/**
 * Header-jobb-szárny: bejelentkezés-állapot szerinti UI.
 *
 * - Bejelentkezetlen: „Belépés" + „Regisztráció" gomb
 * - Bejelentkezett: avatar dropdown — Polcom / Profil / Kijelentkezés
 *
 * A `useAuth` reaktívan követi a Zustand-Persist állapotát (több tabnál is
 * azonnal frissül). Logout után a router /-ra navigál és a `useQuery`
 * gyorstárak invalidálódnak (a `enabled: isLogged` flag-en keresztül).
 */
export function UserMenu() {
  const router = useRouter();
  // Az SSR mindig „bejelentkezetlen"-t lát (nincs localStorage). A kliensen
  // a Zustand-Persist hidratálás közben tölti be a tokent — egészen addig
  // a `useHydrated()` hamis, és az SSR-rel azonos UI-t renderelünk
  // (Belépés + Regisztráció). A hidratálás után váltunk auth-state-re.
  const hydrated = useHydrated();
  const isLogged = useAuth((s) => s.isAuthenticated());
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside-to-close — a dropdown bezárása, ha a felhasználó a panel
  // KÍVÜL kattint vagy ESC-et nyom. WCAG 2.4.3 (Focus Order) szerint az ESC
  // gomb a fókuszt vissza kell adja a triggernek; a Radix-szerű komponensek
  // ezt automatikusan megoldják, itt a minimum-implementációt tartjuk.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!hydrated || !isLogged) {
    return (
      <>
        <Button variant="ghost" size="sm" asChild>
          <Link href="/login">Belépés</Link>
        </Button>
        <Button variant="accent" size="sm" asChild>
          <Link href="/signup">Regisztráció</Link>
        </Button>
      </>
    );
  }

  const initials = (user?.display_name ?? user?.email ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-[var(--color-border)] py-1 pl-1 pr-3 transition-colors hover:bg-[var(--color-surface-subtle)]"
      >
        <Avatar fallback={initials || '?'} className="size-7" />
        <span className="hidden text-sm sm:inline">{user?.display_name ?? user?.email}</span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white shadow-card"
        >
          <div className="border-b border-[var(--color-border)] p-3 text-xs">
            <p className="font-semibold">{user?.display_name}</p>
            <p className="truncate text-[var(--color-foreground-muted)]">{user?.email}</p>
          </div>
          <Link
            href="/shelf"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-subtle)]"
          >
            <User className="size-4" />
            Polcom
          </Link>
          <Link
            href="/stats"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-subtle)]"
          >
            <BarChart3 className="size-4" />
            Statisztika
          </Link>
          <Link
            href={'/account' as Route}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-subtle)]"
          >
            <ShieldCheck className="size-4" />
            Biztonság
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await logout();
              router.push('/');
            }}
            className="flex w-full items-center gap-2 border-t border-[var(--color-border)] px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="size-4" />
            Kijelentkezés
          </button>
        </div>
      ) : null}
    </div>
  );
}
